import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Stack, TextField, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { toast } from 'react-toastify';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getOrderDetail, updateOrderStatus } from '../../api/endpoints/shop';

const statusColor = { pending: 'warning', confirmed: 'info', shipped: 'primary', delivered: 'success', cancelled: 'error', returned: 'default' };

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const res = await getOrderDetail(id);
      setOrder(res.data.data);
      setTracking(res.data.data.trackingNumber || '');
    } catch {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (status) => {
    try {
      await updateOrderStatus(id, status, tracking);
      toast.success('Order status updated');
      fetchOrder();
    } catch {
      toast.error('Failed to update order');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!order) return <Typography sx={{ p: 4 }}>Order not found</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/shop/orders')} sx={{ mb: 2 }}>Back to Orders</Button>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Order #{order.orderNumber}</Typography>
              <Chip label={order.orderStatus} size="small" color={statusColor[order.orderStatus] || 'default'} sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell><Typography variant="body2">{item.productName || item.productId?.name}</Typography></TableCell>
                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1} alignItems="flex-end">
                <Typography variant="body2">Subtotal: {formatCurrency(order.subtotal)}</Typography>
                {order.shippingCharge > 0 && <Typography variant="body2">Shipping: {formatCurrency(order.shippingCharge)}</Typography>}
                <Typography variant="body2">Tax: {formatCurrency(order.tax)}</Typography>
                <Typography variant="h6" fontWeight={700}>Total: {formatCurrency(order.total)}</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Customer</Typography>
              <Typography variant="body2">{order.shippingAddress?.name || order.userId?.name}</Typography>
              <Typography variant="body2" color="text.secondary">{order.shippingAddress?.phone || order.userId?.phone}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Shipping Address</Typography>
              {order.shippingAddress ? (
                <>
                  <Typography variant="body2">{order.shippingAddress.line1}</Typography>
                  {order.shippingAddress.line2 && <Typography variant="body2">{order.shippingAddress.line2}</Typography>}
                  <Typography variant="body2">{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</Typography>
                </>
              ) : <Typography variant="body2" color="text.secondary">No address</Typography>}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Update Status</Typography>
              <Stack spacing={1}>
                {['confirmed', 'shipped', 'delivered', 'cancelled'].filter(s => s !== order.orderStatus).map(s => (
                  <Button key={s} variant="outlined" size="small" fullWidth startIcon={<LocalShippingIcon />} onClick={() => handleStatus(s)}>
                    Mark {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
                <TextField size="small" label="Tracking #" value={tracking} onChange={(e) => setTracking(e.target.value)} fullWidth />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetail;
